import type { Listing, ListingImage } from '@mogzo/shared';
import { useEffect, useRef, useState } from 'react';
import { GamePage } from './GamePage';

/**
 * A listing's game page, full screen over the page, on a computer or a phone: on the developer
 * dashboard (the listing being edited, or as sent) and on the admin site's review page.
 */
export function ListingPreview({
  listing,
  images,
  viewer,
  title,
  note,
  closeLabel,
  onClose,
}: {
  listing: Listing;
  images: Map<string, ListingImage>;
  viewer: string;
  title: string;
  /** Said in the bar, after the title. */
  note: string;
  closeLabel: string;
  onClose: () => void;
}) {
  const [phone, setPhone] = useState(false);
  const back = useRef<HTMLButtonElement>(null);
  useEffect(() => back.current?.focus(), []);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
      className="fixed inset-0 z-50 flex flex-col overflow-auto bg-[#e9e7ef]"
    >
      <div className="sticky top-0 z-10 bg-ink text-white">
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center gap-x-5 gap-y-2 px-6 py-3">
          <strong>{title}</strong>
          <span className="text-[#c9c4dd]">{note}</span>
          <span className="flex-1" />
          <fieldset className="flex gap-0.5 rounded-full bg-ink-soft p-[3px]">
            <legend className="sr-only">Preview on</legend>
            {(
              [
                [false, 'Computer'],
                [true, 'Phone'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={label}
                type="button"
                aria-pressed={phone === value}
                onClick={() => setPhone(value)}
                className={`min-h-8 rounded-full px-3.5 text-[13px] font-bold ${
                  phone === value ? 'bg-white text-ink' : 'text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </fieldset>
          <button
            ref={back}
            type="button"
            onClick={onClose}
            className="min-h-10 rounded-full px-2 font-bold text-brand"
          >
            {closeLabel}
          </button>
        </div>
      </div>
      {phone ? (
        <div className="flex justify-center px-4 py-8">
          {/* The phone scrolls inside its own screen, and the transform keeps the screenshot
              viewer inside it too. */}
          <div className="h-[min(844px,calc(100dvh-8rem))] w-[390px] max-w-full [transform:translateZ(0)] overflow-hidden rounded-[36px] border-[9px] border-[#0b0a1d] shadow-xl">
            <div className="flex h-full flex-col overflow-y-auto [scrollbar-width:none]">
              <GamePage listing={listing} images={images} viewer={viewer} phone />
            </div>
          </div>
        </div>
      ) : (
        <GamePage listing={listing} images={images} viewer={viewer} phone={false} />
      )}
    </div>
  );
}
